import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { Text } from '@/components/ui/text/Text';
import { IconScout } from '@/components/ui/images/IconScout';
import { LocalIcon } from '@/components/ui/images/LocalIcon';
import { TABLE_PALETTE, ROW_BORDER_STYLE, TABLE_ACTION_VARIANT_COLOR } from './Table.constants';
import { resolveCellContent, getAlignmentClasses, getColumnWidth } from './Table.utils';
import { TableCell } from './_TableCell';
import { ExpandedRowDetails } from './ExpandedRowDetails';
import type { TableColumn, SortState, ExpandableConfig, BulkActionItem } from './Table.types';
import { colors } from '@/assets/styles/colors';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { InfoBubble } from '@/components/ui/tooltips/InfoBubble';


export interface TableContainerProps<T> {
  columns: TableColumn<T>[];
  selectable: boolean;
  allVisibleSelected: boolean;
  onToggleSelectAll: () => void;
  onSort: (col: TableColumn<T>) => void;
  sort: SortState | undefined;
  children: React.ReactNode;
  disabled?: boolean;
  minTableWidth: number;
  expandable?: ExpandableConfig<T>;
  hasSelection?: boolean;
  bulkActions?: BulkActionItem[];
  isIndeterminate?: boolean;
  selectAllDisabled?: boolean;
  fontSize?: string | number;
  /** Estira la tabla al alto disponible; se usa con el estado vacío para centrarlo. */
  fillHeight?: boolean;
}

interface BulkActionButtonProps {
  action: BulkActionItem;
  onSelect: (action: BulkActionItem) => void;
}

/** Deriva si se muestra la columna de expandir y en qué extremo, a partir del mismo `expandable`
 *  que reciben tanto TableContainer (encabezado) como TableBody (celdas) — evita recalcular la
 *  misma expresión en los dos componentes. */
function resolveExpandColumn<T>(expandable?: ExpandableConfig<T>) {
  return {
    showExpandColumn: Boolean(expandable) && expandable?.showExpandColumn !== false,
    expandPosition: expandable?.position ?? 'left',
  } as const;
}

// Opción individual del menú masivo. Mismo patrón que ActionMenuOption: el wrapper recibe los
// eventos de mouse para que el tooltip funcione incluso con el botón deshabilitado.
function BulkActionButton({ action, onSelect }: BulkActionButtonProps) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const hasTooltip = Boolean(action.tooltip);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={hasTooltip ? () => setShowTooltip(true) : undefined}
      onMouseLeave={hasTooltip ? () => setShowTooltip(false) : undefined}
    >
      <button
        type="button"
        disabled={action.disabled}
        className={`flex items-center gap-3 px-3 py-1 rounded-lg text-left w-full ${
          action.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
        }`}
        onClick={() => onSelect(action)}
        data-testid={`bulk-action-${action.label}`}
      >
        <IconScout
          name={action.icon}
          size={24}
          color={
            action.disabled
              ? colors.grays.neutral99
              : TABLE_ACTION_VARIANT_COLOR[action.variant ?? 'default']
          }
        />
        <Text variant="subheader-regular" color={action.disabled ? colors.grays.neutral99 : colors.grays.neutral33}>
          {action.label}
        </Text>
      </button>
      {action.tooltip && showTooltip && (
        <Tooltip body={action.tooltip} anchorRef={wrapperRef} placement="left" />
      )}
    </div>
  );
}

/**
 * Internal component for table container with header
 * Maintains responsivity with overflow-x-auto
 */
export function TableContainer<T extends Record<string, any>>({
  columns,
  selectable,
  allVisibleSelected,
  onToggleSelectAll,
  onSort,
  sort,
  children,
  disabled = false,
  minTableWidth,
  expandable,
  hasSelection = false,
  bulkActions = [],
  isIndeterminate = false,
  selectAllDisabled = false,
  fontSize,
  fillHeight = false,
}: TableContainerProps<T>) {
  const headerTextStyle = fontSize !== undefined ? { fontSize } : undefined;
  const { t: translate } = useTranslation();
  const [bulkMenuOpen, setBulkMenuOpen] = React.useState(false);
  const bulkMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!hasSelection) setBulkMenuOpen(false);
  }, [hasSelection]);

  React.useEffect(() => {
    if (!bulkMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(event.target as Node)) {
        setBulkMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bulkMenuOpen]);

  const showBulkTrigger = hasSelection && bulkActions.length > 0;

  const { showExpandColumn, expandPosition } = resolveExpandColumn(expandable);
  const expandColumnHeader = (
    <th className="px-4 py-2 w-10" style={ROW_BORDER_STYLE}>
      {/* Empty header for expand column */}
    </th>
  );

  return (
    // `grow` ocupa el alto sobrante cuando el Card estira (grids items-stretch), para que el footer
    // de paginación quede abajo en vez de pegado a la última fila.
    <div className="grow" style={{ position: 'relative' }}>
      <div
        className={`overflow-x-auto ${fillHeight ? 'h-full' : ''}`}
        style={{
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <table
          className="border-collapse"
          style={{
            borderSpacing: 0,
            minWidth: `${minTableWidth}px`,
            width: '100%',
            height: fillHeight ? '100%' : undefined,
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr className="text-left">
              {showExpandColumn && expandPosition === 'left' && expandColumnHeader}
              {selectable && (
                <th
                  className="px-4 py-2"
                  style={{ ...ROW_BORDER_STYLE, width: bulkActions.length ? '64px' : '40px' }}
                >
                  <div className="flex items-center gap-1">
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={isIndeterminate}
                      onChange={() => !disabled && !selectAllDisabled && onToggleSelectAll()}
                      data-testid="checkbox-all"
                      disabled={disabled || selectAllDisabled}
                    />
                    {showBulkTrigger && (
                      <button
                        type="button"
                        className="flex items-center"
                        onClick={() => setBulkMenuOpen((v) => !v)}
                        data-testid="bulk-actions-trigger"
                        aria-label={translate('common:table.bulkActionsLabel')}
                        aria-expanded={bulkMenuOpen}
                      >
                        <IconScout name="angleDown" size={14} color={colors.primary.main} />
                      </button>
                    )}
                  </div>
                </th>
              )}
              {columns.map((column) => {
                const isActive = sort?.key === String(column.key);
                const baseCellClass = 'px-4 py-2 font-medium select-none whitespace-nowrap';
                const interactiveClass = column.sortable ? 'cursor-pointer' : '';
                const align = column.align ?? 'left';
                const columnWidth = getColumnWidth(column);
                const ariaSort = column.sortable
                  ? isActive
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined;
                const { text: alignClass, justify: justifyClass } = getAlignmentClasses(align);
                return (
                  <th
                    key={String(column.key)}
                    onClick={() => column.sortable && !disabled && onSort(column)}
                    onKeyDown={(event) => {
                      if (column.sortable && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onSort(column);
                      }
                    }}
                    tabIndex={column.sortable && !disabled ? 0 : -1}
                    aria-sort={ariaSort as any}
                    scope="col"
                    className={`${baseCellClass} ${interactiveClass} ${alignClass} focus:outline-none focus:ring-2`}
                    aria-disabled={disabled || undefined}
                    style={{
                      ...ROW_BORDER_STYLE,
                      width: column.width || `${columnWidth}px`,
                      minWidth: `${columnWidth}px`,
                      maxWidth: column.width || `${columnWidth}px`,
                    }}
                  >
                    <div className={`flex gap-1 items-center ${justifyClass}`}>
                      {column.header?.includes('\n') ? (
                        <div className="flex flex-col items-center text-center">
                          {column.header.split('\n').map((headerLine) => (
                            <Text
                              key={headerLine}
                              variant="subheader-medium"
                              color={TABLE_PALETTE.headerText}
                              style={headerTextStyle}
                              className="truncate"
                            >
                              {headerLine}
                            </Text>
                          ))}
                        </div>
                      ) : (
                        <Text
                          variant="subheader-medium"
                          color={TABLE_PALETTE.headerText}
                          style={headerTextStyle}
                          className="truncate"
                        >
                          {column.header}
                        </Text>
                      )}
                      {column.headerInfoText && (
                        // El <th> ordena al hacer click/Enter: sin stopPropagation, consultar la
                        // ayuda de la columna reordenaría la tabla.
                        <span
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <InfoBubble
                            text={column.headerInfoText}
                            title={column.headerInfoTitle}
                            placement="bottom"
                            // El contenedor de la tabla tiene overflow-x: auto y recorta en ambos
                            // ejes: con la estrategia absolute por defecto, el tooltip de las
                            // últimas columnas queda cortado contra el borde derecho. 'auto'
                            // posiciona en fixed y ajusta las coordenadas al viewport.
                            strategy="auto"
                            data-testid={`column-info-${String(column.key)}`}
                          />
                        </span>
                      )}
                      {column.sortable && (
                        <IconScout
                          name={isActive ? (sort.direction === 'asc' ? 'arrowUp' : 'arrowDown') : 'arrowUp'}
                          size="lg"
                          color={TABLE_PALETTE.arrow}
                          className={`transition-transform ${isActive ? '' : 'opacity-40'}`}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
              {showExpandColumn && expandPosition === 'right' && expandColumnHeader}
            </tr>
          </thead>
          {children}
        </table>
      </div>
      {bulkMenuOpen && showBulkTrigger && (
        <div
          ref={bulkMenuRef}
          className="absolute bg-white rounded-2xl p-1 flex flex-col"
          style={{
            top: '46px',
            left: '16px',
            boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.15)',
            minWidth: 'max-content',
            zIndex: 50,
          }}
          data-testid="bulk-actions-menu"
        >
          {bulkActions.map((action) => (
            <BulkActionButton
              key={action.label}
              action={action}
              onSelect={(selected) => {
                if (selected.disabled) return;
                selected.onClick();
                setBulkMenuOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export interface TableBodyProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  selectable: boolean;
  selectedIds: Set<string | number>;
  toggleRow: (row: T) => void;
  getRowId: (row: T) => string | number;
  disabled?: boolean;
  expandable?: ExpandableConfig<T>;
  expandedRowKeys?: Set<string | number>;
  toggleRowExpanded?: (rowId: string | number) => void;
  emptyMessage?: string;
  /** Optional predicate to disable the checkbox for specific rows */
  isRowSelectable?: (row: T) => boolean;
  /** Optional per-row text color for the cell content (undefined keeps the default) */
  getRowTextColor?: (row: T) => string | undefined;
  fontSize?: string | number;
}

/**
 * Internal component for table body with rows and cells
 * Uses TableCell for consistent rendering with improvements
 */
export function TableBody<T extends Record<string, any>>({
  data,
  columns,
  selectable,
  selectedIds,
  toggleRow,
  getRowId,
  disabled = false,
  expandable,
  expandedRowKeys,
  toggleRowExpanded,
  emptyMessage,
  isRowSelectable,
  getRowTextColor,
  fontSize,
}: TableBodyProps<T>) {
  const { t: translate } = useTranslation();

  const { showExpandColumn, expandPosition } = resolveExpandColumn(expandable);

  const renderExpandCell = (
    id: string | number,
    isExpanded: boolean,
    canExpand: boolean,
  ) => (
    <td className="px-4 py-2">
      <button
        onClick={() => !disabled && canExpand && toggleRowExpanded?.(id)}
        className="flex items-center justify-center w-8 h-8"
        disabled={disabled || !canExpand}
        aria-label={isExpanded ? 'Colapsar fila' : 'Expandir fila'}
        data-testid={`expand-button-${id}`}
      >
        <IconScout
          name={isExpanded ? 'angleUp' : 'angleDown'}
          size="xl"
          color={(
            disabled || !canExpand
          ) ? colors.grays.neutralCC : colors.important.main}
        />
      </button>
    </td>
  );

  return (
    <tbody>
      {data.length === 0 && (
        <tr>
          <td
            colSpan={
              columns.length +
              (selectable ? 1 : 0) +
              (showExpandColumn ? 1 : 0)
            }
            className="px-4 py-8 text-center"
            style={ROW_BORDER_STYLE}
          >
            <div className="flex flex-col items-center gap-3">
              <LocalIcon name="emptyList" className="w-40 h-auto" />
              <Text variant="body-title-medium" textAlign="center">{emptyMessage ?? translate('common:table.emptyTitle')}</Text>
              {!emptyMessage && (
                <Text variant="body-regular" textAlign="center">
                  {translate('common:table.emptyDescription')}
                  <br />
                  {translate('common:table.emptySearchHint')}
                </Text>
              )}
            </div>
          </td>
        </tr>
      )}
      {data.map((row) => {
        const id = getRowId(row);
        const isExpanded = expandedRowKeys?.has(id) ?? false;
        const canExpand = expandable?.rowExpandable ? expandable.rowExpandable(row) : true;
        const rowSelectable = isRowSelectable ? isRowSelectable(row) : true;
        const showExpandable = expandable && canExpand;
        const rowTextColor = getRowTextColor?.(row);
        
        return (
          <React.Fragment key={String(id)}>
            <tr
              className="transition-colors hover:bg-[var(--row-hover)]"
              style={ROW_BORDER_STYLE}
            >
              {showExpandColumn && expandPosition === 'left' && renderExpandCell(id, isExpanded, canExpand)}
              {selectable && (
                <td className="px-4 py-2">
                  <Checkbox
                    checked={selectedIds.has(id)}
                    onChange={() => !disabled && rowSelectable && toggleRow(row)}
                    data-testid={`checkbox-${id}`}
                    disabled={disabled || !rowSelectable}
                  />
                </td>
              )}
              {columns.map((column) => {
                const align = column.align ?? 'left';
                const columnWidth = getColumnWidth(column);
                const strategy = column.overflowStrategy ?? 'truncate';
                const rawValue: any = column.render
                  ? column.render(row)
                  : (row as any)[column.key as keyof T];
                const value = resolveCellContent(rawValue, rowTextColor, fontSize);
                const { text: bodyAlignClass } = getAlignmentClasses(align);
                
                const cellContent = (
                  <TableCell strategy={strategy} width={column.width || columnWidth} align={align}>
                    {value}
                  </TableCell>
                );
                
                return (
                  <td
                    key={String(column.key)}
                    className={`px-4 py-2 ${bodyAlignClass}`}
                    style={{
                      ...ROW_BORDER_STYLE,
                      width: column.width || `${columnWidth}px`,
                      minWidth: `${columnWidth}px`,
                      maxWidth: column.width || `${columnWidth}px`,
                    }}
                  >
                    {cellContent}
                  </td>
                );
              })}
              {showExpandColumn && expandPosition === 'right' && renderExpandCell(id, isExpanded, canExpand)}
            </tr>
            {showExpandable && isExpanded && (
              <tr
                style={{
                  ...ROW_BORDER_STYLE,
                  ...(expandable.highlightBackground === false
                    ? {}
                    : { backgroundColor: TABLE_PALETTE.expandedRowBackground }),
                }}
              >
                <td
                  colSpan={
                    columns.length +
                    (selectable ? 1 : 0) +
                    (showExpandColumn ? 1 : 0)
                  }
                  className="py-0"
                  style={ROW_BORDER_STYLE}
                >
                  <div className="pl-24">
                    {expandable.renderExpanded
                      ? expandable.renderExpanded(row)
                      : expandable.getExpandedFields
                        ? <ExpandedRowDetails fields={expandable.getExpandedFields(row)} />
                        : null
                    }
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}
